// Simple pub/sub bridge so non-React services (Axios) can ask the UI
// to show a retry dialog and await the user's decision.

type RetryListener = (message: string, resolve: (shouldRetry: boolean) => void) => void;

const DEFAULT_MESSAGE = "We're facing a technical issue reaching the server.";

let listener: RetryListener | null = null;
let isOpen = false;

export const registerNetworkErrorListener = (fn: RetryListener) => {
  listener = fn;
};

export const unregisterNetworkErrorListener = (fn: RetryListener) => {
  if (listener === fn) {
    listener = null;
  }
};

// Resolves true if the user chose to retry, false otherwise.
// Skips showing a duplicate dialog if one is already open.
export const askToRetry = (message: string = DEFAULT_MESSAGE): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!listener || isOpen) {
      resolve(false);
      return;
    }
    isOpen = true;
    listener(message, (shouldRetry) => {
      isOpen = false;
      resolve(shouldRetry);
    });
  });
};

// True when the failure looks like "no response received" or a server-side error.
export const isRetryableError = (error: any): boolean => {
  if (!error) return false;
  if (error instanceof TypeError) return true; // fetch network failure
  if (error.code === "ECONNABORTED") return true; // axios timeout
  if (error.request && !error.response) return true; // axios: no response received
  if (error.response && typeof error.response.status === "number" && error.response.status >= 500) return true;
  return false;
};
